/**
 * Positions with and without kings for the path (docs/PATH-V2.md §6.6, docs/path-v2/CONTRACTS.md §3.2).
 *
 * A path position may lack one or both kings (mini-games «Пешечный бой», «Игра на уничтожение», flags). Such a
 * position loads into chess.js with `{ skipValidation: true }`: a side without a king moves pseudo-legally (chess.js
 * never finds its king attacked). `isStalemate()` / `isDraw()` are NEVER consulted for kingless positions — the
 * mini-game rules decide. With both kings the position is plain chess.js (full validation, full legality).
 *
 * Pure and isomorphic. Squares are algebraic (`e4`), moves UCI (`e7e8q`); a promotion without a letter is a queen.
 */
import { Chess, type Square as CjsSquare } from 'chess.js';
import type { Color, PieceType, Square } from '@gambit/shared';
import { parsePlacement, squareIndex, squareName } from '../analysis/board.ts';

export interface PathPiece {
  type: PieceType;
  color: Color;
}

/** Both kings are on the board (one of each colour). */
export function hasBothKings(fen: string): boolean {
  const board = parsePlacement(fen);
  let w = 0;
  let b = 0;
  for (const p of board) {
    if (p?.type === 'k') {
      if (p.color === 'w') w++;
      else b++;
    }
  }
  return w === 1 && b === 1;
}

/** The position is a kingless one (at least one king missing) — it must load with `skipValidation`. */
export function isKingless(fen: string): boolean {
  return !hasBothKings(fen);
}

/**
 * Loads a position into chess.js: plain for positions with both kings, `skipValidation` for kingless ones.
 * Throws on a malformed FEN (or an invalid position with kings).
 */
export function loadPosition(fen: string): Chess {
  return isKingless(fen) ? new Chess(fen, { skipValidation: true }) : new Chess(fen);
}

/** Why a FEN cannot be used as a path position, or null when it can (and whether it is kingless). */
export function positionProblem(fen: string, kinglessFlag?: boolean): string | null {
  let kings: ReturnType<typeof parsePlacement>;
  try {
    kings = parsePlacement(fen).filter((p) => p?.type === 'k');
  } catch (e) {
    return `illegal fen: ${(e as Error).message}`;
  }
  if (kings.filter((k) => k?.color === 'w').length > 1 || kings.filter((k) => k?.color === 'b').length > 1) return 'illegal fen: two kings of one colour';
  const kingless = isKingless(fen);
  try {
    loadPosition(fen);
  } catch (e) {
    return `illegal fen: ${(e as Error).message}`;
  }
  if (kingless && !kinglessFlag) return 'position has no kings but the task is not marked kingless';
  if (!kingless && kinglessFlag) return 'task is marked kingless but the position has both kings';
  return null;
}

/** Side to move of a FEN. */
export function sideToMove(fen: string): Color {
  return fen.trim().split(/\s+/)[1] === 'b' ? 'b' : 'w';
}

/** The same position with `color` to move and no en passant square (a kingless side keeps moving: flags). */
export function withTurn(fen: string, color: Color): string {
  const f = fen.trim().split(/\s+/);
  f[1] = color;
  f[3] = '-';
  while (f.length < 6) f.push(f.length === 4 ? '0' : '1');
  return f.join(' ');
}

/** The piece on `sq`, or null. */
export function pieceAt(fen: string, sq: Square): PathPiece | null {
  const idx = squareIndex(sq);
  if (idx < 0) return null;
  const p = parsePlacement(fen)[idx];
  return p ? { type: p.type, color: p.color } : null;
}

/**
 * The UCI of a SAN made by the piece on `from` (chess.js spelling: `Nxe5+`, `exd8=Q#`, `O-O-O`): the destination is the
 * last square named, a castle is the king's two-square step.
 */
function uciOfSan(san: string, from: string): string {
  if (san.startsWith('O-O')) return `${from}${san.startsWith('O-O-O') ? 'c' : 'g'}${from.charAt(1)}`;
  const m = /([a-h][1-8])(?:=([QRBN]))?[+#]?$/.exec(san);
  if (!m) throw new Error(`uciOfSan: ${san}`);
  return `${from}${m[1]}${m[2]?.toLowerCase() ?? ''}`;
}

/**
 * The legal moves of the piece on `from` (UCI, in chess.js's order), read off the square's SAN list. Never chess.js's
 * verbose moves: each one costs a whole move generation of its own (its SAN's disambiguation) plus two FENs — the
 * position's verbose list was half of the generator's time (notGift@3 ≈ 30 ms a task on the CI's CPU). Same moves,
 * same order (the fingerprint of every recipe version, its reasons and red arrows is unchanged).
 */
function movesFrom(chess: Chess, from: string): string[] {
  return chess.moves({ square: from as CjsSquare }).map((san) => uciOfSan(san, from));
}

/** Every legal move of the side to move (UCI; pseudo-legal for a side without a king). */
export function legalMovesUci(fen: string): string[] {
  const chess = loadPosition(fen);
  const turn = chess.turn();
  const board = parsePlacement(fen);
  const out: string[] = [];
  const castles: string[] = [];
  // chess.js's order: squares a8 … h1, each square's moves as generated, the castles last
  for (let rank = 7; rank >= 0; rank--) {
    for (let file = 0; file < 8; file++) {
      const idx = rank * 8 + file;
      if (board[idx]?.color !== turn) continue;
      const from = squareName(idx);
      for (const san of chess.moves({ square: from as CjsSquare })) (san.startsWith('O-O') ? castles : out).push(uciOfSan(san, from));
    }
  }
  return [...out, ...castles];
}

/** The legal move `uci` in the generated spelling (a promotion without a letter is a queen), or null. */
function findUci(chess: Chess, uci: string): string | null {
  const from = uci.slice(0, 2);
  const to = uci.slice(2, 4);
  const promo = uci.length > 4 ? uci.charAt(4).toLowerCase() : null;
  const cands = movesFrom(chess, from).filter((m) => m.slice(2, 4) === to);
  const first = cands[0];
  if (first === undefined) return null;
  if (first.length === 4) return first;
  return cands.find((m) => m.charAt(4) === (promo ?? 'q')) ?? null;
}

/** Is `uci` a legal move here (pseudo-legal for a side without a king)? */
export function isLegalMove(fen: string, uci: string): boolean {
  try {
    return findUci(loadPosition(fen), uci) !== null;
  } catch {
    return false;
  }
}

/** Normalises a UCI move to the generated spelling (`e7e8` → `e7e8q`); null when illegal. */
export function normalizeUci(fen: string, uci: string): string | null {
  try {
    return findUci(loadPosition(fen), uci);
  } catch {
    return null;
  }
}

/** The position after `uci` (both kinds of positions). Returns null for an illegal move. */
export function tryApplyMove(fen: string, uci: string): string | null {
  try {
    const chess = loadPosition(fen);
    const m = findUci(chess, uci);
    if (!m) return null;
    chess.move({ from: m.slice(0, 2), to: m.slice(2, 4), ...(m.length > 4 ? { promotion: m.charAt(4) } : {}) });
    return chess.fen();
  } catch {
    return null;
  }
}

/** The position after `uci` (both kinds of positions). Throws on an illegal move. */
export function applyMove(fen: string, uci: string): string {
  const next = tryApplyMove(fen, uci);
  if (next === null) throw new Error(`applyMove: illegal move ${uci} in ${fen}`);
  return next;
}

/** What the move `uci` captures (the square of the victim — differs from `to` for en passant), or null. */
export function captureOf(fen: string, uci: string): { square: Square; piece: PathPiece } | null {
  try {
    const m = findUci(loadPosition(fen), uci);
    if (!m) return null;
    const board = parsePlacement(fen);
    const mover = board[squareIndex(m.slice(0, 2))];
    const to = m.slice(2, 4);
    const onTo = board[squareIndex(to)];
    if (!mover) return null;
    // a pawn stepping sideways onto an empty square takes en passant: the passed pawn on its own square
    const ep = !onTo && mover.type === 'p' && to.charAt(0) !== m.charAt(0);
    const square = (ep ? `${to.charAt(0)}${m.charAt(1)}` : to) as Square;
    const victim = ep ? board[squareIndex(square)] : onTo;
    if (!victim || victim.color === mover.color) return null;
    return { square, piece: { type: victim.type, color: victim.color } };
  } catch {
    return null;
  }
}

/** The side to move is in check (always false for a side without a king). */
export function isCheck(fen: string): boolean {
  if (isKingless(fen)) {
    const turn = sideToMove(fen);
    if (!parsePlacement(fen).some((p) => p?.type === 'k' && p.color === turn)) return false;
  }
  return loadPosition(fen).isCheck();
}

/** The side to move is checkmated (a side without a king is never mated). */
export function isMate(fen: string): boolean {
  return isCheck(fen) && legalMovesUci(fen).length === 0;
}

/** All 64 square names a1..h8 (for dims and scans). */
export const ALL_SQUARES: readonly Square[] = Array.from({ length: 64 }, (_, i) => squareName(i));
